import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREVIEW_IMG = "/cheat-sheet-preview.png";
const SUMMARY_URL = "/code-echo-summary-sheet.pdf";

function triggerDownload() {
  const a = document.createElement("a");
  a.href = SUMMARY_URL;
  a.download = "Code-Echo-First-Aid-Summary-Sheet.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function ResourceHub() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const closeAll = () => {
    setOpen(false);
    setLead({ name: "", email: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email) {
      toast.error(t.res_err_required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/resource-leads`, { ...lead, is_responder: false });
      triggerDownload();
      closeAll();
    } catch (err) {
      toast.error(t.res_err_toast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="resources" data-testid="resources-section">
      <div className="grid grid-cols-[auto_1fr] gap-5 md:gap-8 items-stretch border-x border-b border-white/10 bg-[#0d0e14] p-4 md:p-6">
        {/* Summary sheet preview (small, left, matches column height) */}
        <div className="flex items-stretch">
          <img
            src={PREVIEW_IMG}
            alt="Code Echo First Aid Summary Sheet preview"
            className="h-full w-auto max-h-[280px] md:max-h-[300px] object-contain rounded-sm border border-white/10 bg-white"
          />
        </div>

        {/* Text + button */}
        <div className="flex flex-col justify-center">
          <h3 className="font-heading text-2xl md:text-3xl font-medium text-white leading-tight">
            {t.res_title}
          </h3>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 leading-relaxed">
            {t.res_sub}
          </p>
          <button
            onClick={() => setOpen(true)}
            data-testid="open-resource-modal"
            className="group relative mt-6 inline-flex self-start overflow-hidden rounded-sm bg-[#D4AF37] px-6 md:px-7 py-3.5 text-xs sm:text-sm font-bold uppercase tracking-[0.18em] text-[#0B0C10] transition-all duration-300 hover:glow-gold"
          >
            <span className="relative z-10 flex items-center gap-3">
              <Download className="h-4 w-4" /> {t.res_btn}
            </span>
            <span className="absolute inset-0 -translate-y-full bg-[#e6c65a] transition-transform duration-300 group-hover:translate-y-0" />
          </button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeAll())}>
        <DialogContent
          className="border-white/15 bg-[#0d0e14]/95 backdrop-blur-xl text-white sm:rounded-sm"
          data-testid="resource-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-medium">
              {t.res_modal_title}
            </DialogTitle>
            <DialogDescription className="text-zinc-300">
              {t.res_modal_desc}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="grid gap-5 mt-2" data-testid="resource-form">
            <div className="grid gap-2">
              <Label className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-300">{t.res_name}</Label>
              <Input
                data-testid="resource-name"
                value={lead.name}
                onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))}
                placeholder={t.res_name_ph}
                className="bg-transparent border-white/20 text-white h-12 rounded-sm focus-visible:ring-1 focus-visible:ring-[#0057B7] focus-visible:border-[#0057B7] placeholder:text-zinc-600"
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-300">{t.res_email}</Label>
              <Input
                data-testid="resource-email"
                type="email"
                value={lead.email}
                onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                placeholder={t.res_email_ph}
                className="bg-transparent border-white/20 text-white h-12 rounded-sm focus-visible:ring-1 focus-visible:ring-[#0057B7] focus-visible:border-[#0057B7] placeholder:text-zinc-600"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="resource-submit"
              className="group relative mt-1 overflow-hidden rounded-sm bg-[#0057B7] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.18em] text-white transition-all duration-300 hover:glow-blue disabled:opacity-60"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t.res_submit}
              </span>
              <span className="absolute inset-0 -translate-y-full bg-[#003f86] transition-transform duration-300 group-hover:translate-y-0" />
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
